import React, { useRef, useState } from "react";
import {
  FormInstance,
  Upload,
  Button,
  Alert,
  App,
  Modal,
  Input,
  Tag,
  Space,
  Divider,
} from "antd";
import clsx from "clsx";
import {
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { RcFile } from "antd/es/upload";
import ImgCrop, { ImgCropProps } from "antd-img-crop";
import { getMessageInError } from "../../../hooks/fetch";
import { getBase64 } from "../../../utils";
import { useUploadFile } from "../../../hooks/file";
import { useMessageError } from "../../../hooks/common";
import { useTranslation } from "react-i18next";
import SimpleImageEditor from "../SimpleImageEditor";
import { useAuth } from "../../../store/auth";
import {
  FileMetadataBody,
  FileTag,
  FileWithMetadata,
  useFileUpdate,
} from "../../../hooks/api/responses";

import styles from "./index.module.scss";

const { TextArea } = Input;

export const getFullFileUrl = (url: string | undefined) =>
  url ? `${process.env.REACT_APP_AMAZON_S3_URL}/${url}` : "";

interface ImageUploadWithMetadataProps {
  name: string;
  fileList: FileWithMetadata[];
  setFileList: React.Dispatch<React.SetStateAction<FileWithMetadata[]>>;
  readOnly?: boolean;
  required?: boolean;
  form?: FormInstance;
  accept?: string;
  uploadUrl?: string;
  multiple?: boolean;
  className?: string;
  cropperProps?: Partial<ImgCropProps>;
  wide?: boolean;
  onFileMetadataChange?: (
    fileId: string,
    metadata: Partial<FileMetadataBody>,
  ) => void;
  pendingMetadata?: Record<string, Partial<FileMetadataBody>>;
}

interface PreviewModal {
  image: string;
  title: string;
  visible: boolean;
}

function ImageUploadWithMetadata({
  form,
  name,
  fileList,
  setFileList,
  readOnly = false,
  required = false,
  accept = ".jpg,.png,.jpeg",
  uploadUrl = "file/upload",
  multiple = true,
  className,
  cropperProps,
  wide = false,
  onFileMetadataChange,
  pendingMetadata,
}: ImageUploadWithMetadataProps) {
  const { message } = App.useApp();
  const uploadRef = useRef<HTMLButtonElement | null>(null);
  const uploader = useUploadFile(uploadUrl);
  const [preview, setPreview] = useState<PreviewModal>({
    visible: false,
    image: "",
    title: "",
  });
  const { t } = useTranslation();
  const { user } = useAuth();
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<FileWithMetadata | null>(null);
  const [currentEditIndex, setCurrentEditIndex] = useState<number>(0);
  const fileUpdate = useFileUpdate();

  useMessageError([uploader]);

  const cropperParams: Partial<ImgCropProps> = {
    rotationSlider: true,
    showReset: true,
    resetText: t("common.reset"),
    modalTitle: t("common.edit"),
    aspect: wide ? 16 / 9 : undefined,
    ...cropperProps,
  };

  /** Update file metadata */
  const updateFile = (uid: string, updates: Partial<FileWithMetadata>) => {
    setFileList((prev) =>
      prev.map((f) => (f.uid === uid ? { ...f, ...updates } : f)),
    );
  };

  /** Handlers */
  const handleCancelPreview = () => setPreview({ ...preview, visible: false });

  const onRemove = (uid: string) => {
    setFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  const onDownload = (file: FileWithMetadata) => {
    const originalBeforeUnload = window.onbeforeunload;
    window.onbeforeunload = null;

    const link = document.createElement("a");
    const url = file.url?.startsWith("http")
      ? file.url
      : getFullFileUrl(file.url);
    link.href = url || "";
    link.download = file.name || "download";

    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.onbeforeunload = originalBeforeUnload;
    }, 100);
  };

  const beforeUpload = async (file: RcFile, index?: number) => {
    /** Validation */
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      message.error(t("common.uploadJPG/PNG"));
      return false;
    }

    if (!(file.size / 1024 / 1024 < 10)) {
      message.error("common.imageMustSmaller");
      return false;
    }

    /** Create new file object */
    const newFile: FileWithMetadata = {
      originFileObj: file,
      uid: file.uid || Date.now().toString(),
      name: file.name,
      status: "done",
      note: "",
      tags: [],
    };

    newFile.preview = (await getBase64(file)) as string;

    if (index !== null && index !== undefined) {
      const newFileList = [...fileList];
      newFileList[index] = { ...newFileList[index], ...newFile };
      setFileList(newFileList);
    } else if (multiple) {
      setFileList([...fileList, newFile]);
    } else {
      setFileList([newFile]);
    }

    /** Upload to API */
    const formData = new FormData();
    formData.append("file", newFile.originFileObj as Blob);

    uploader.fetch(formData).then((res) => {
      if (res?.id) {
        setFileList((prevFileList) =>
          prevFileList.map((item) =>
            item.uid === newFile.uid
              ? {
                ...item,
                uid: res.id,
                url: res.url || res.originalName,
                id: res.id,
              }
              : item,
          ),
        );
      }
    });

    return false;
  };

  const handleNoteChange = (uid: string, note: string) => {
    setFileList((prevFileList) => {
      const file = prevFileList.find((f) => f.uid === uid);
      console.log("handleNoteChange found file:", file?.id, file?.uid);

      if (file?.id && onFileMetadataChange) {
        onFileMetadataChange(file.id, { note });
      } else if (file?.uid && onFileMetadataChange) {
        onFileMetadataChange(file.uid, { note });
      }

      return prevFileList.map((f) => (f.uid === uid ? { ...f, note } : f));
    });
  };

  const handleRemoveTag = (uid: string, tagId: string) => {
    setFileList((prevFileList) => {
      const file = prevFileList.find((f) => f.uid === uid);
      if (!file) return prevFileList;

      const remainingTags = (file.tags || []).filter(
        (t: FileTag) => t.id !== tagId,
      );

      const fileId = file.id || file.uid;
      if (fileId && onFileMetadataChange) {
        onFileMetadataChange(fileId, { tags: remainingTags });
      }

      return prevFileList.map((f) =>
        f.uid === uid ? { ...f, tags: remainingTags } : f,
      );
    });
  };

  const onEdit = (file: FileWithMetadata, index: number) => {
    const imageToEdit =
      file.preview ||
      (file.url?.startsWith("http") ? file.url : getFullFileUrl(file.url)) ||
      file.thumbUrl ||
      "";

    setEditingFile({
      ...file,
      preview: imageToEdit,
    });
    setCurrentEditIndex(index);
    setEditorOpen(true);
  };

  const handlePrevImage = () => {
    const newIndex =
      currentEditIndex > 0 ? currentEditIndex - 1 : fileList.length - 1;
    const prevFile = fileList[newIndex];
    const imageToEdit =
      prevFile.preview ||
      (prevFile.url?.startsWith("http")
        ? prevFile.url
        : getFullFileUrl(prevFile.url)) ||
      prevFile.thumbUrl ||
      "";

    setCurrentEditIndex(newIndex);
    setEditingFile({
      ...prevFile,
      preview: imageToEdit,
    });
  };

  const handleNextImage = () => {
    const newIndex =
      currentEditIndex < fileList.length - 1 ? currentEditIndex + 1 : 0;
    const nextFile = fileList[newIndex];
    const imageToEdit =
      nextFile.preview ||
      (nextFile.url?.startsWith("http")
        ? nextFile.url
        : getFullFileUrl(nextFile.url)) ||
      nextFile.thumbUrl ||
      "";

    setCurrentEditIndex(newIndex);
    setEditingFile({
      ...nextFile,
      preview: imageToEdit,
    });
  };

  const handleSaveEdit = async (result: {
    id: string;
    url: string;
    originalName: string;
  }) => {
    const oldFileId = editingFile?.id || editingFile?.uid;
    const newFileId = result.id;

    setFileList((prev) =>
      prev.map((file) =>
        file.uid === editingFile?.uid
          ? {
            ...file,
            uid: result.id,
            id: result.id,
            name: result.originalName,
            url: result.url,
            preview: result.url,
            status: "done" as const,
          }
          : file,
      ),
    );
    if (editingFile?.note || editingFile?.tags?.length) {
      try {
        await fileUpdate.fetch(
          {
            note: editingFile.note,
            tags: editingFile.tags,
          },
          newFileId,
        );
      } catch (error) {
        console.error("Failed to update file metadata:", error);
      }
    }

    setEditorOpen(false);
  };

  return (
    <div className={clsx(styles.metadataUploader, className)}>
      {/* File List with Metadata */}
      {fileList.map((file, index) => (
        <React.Fragment key={file.uid}>
          <div
            className={styles.fileCard}
            style={{
              display: "flex",
              gap: 16,
            }}
          >
            {/* Image Preview */}
            <div style={{ flexShrink: 0 }}>
              <div
                className={styles.imageWrapper}
                onClick={() => onEdit(file, index)}
                style={{
                  width: 220,
                  height: 220,
                  borderRadius: 6,
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                <img
                  src={
                    file.preview ||
                    (file.url?.startsWith("http")
                      ? file.url
                      : getFullFileUrl(file.url)) ||
                    file.thumbUrl
                  }
                  alt={file.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>

            {/* Metadata Section */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Description */}
              <TextArea
                value={file.note || ""}
                onChange={(e) => handleNoteChange(file.uid, e.target.value)}
                placeholder="Add description..."
                disabled={readOnly}
                rows={2}
                style={{
                  width: "100%",
                  marginBottom: 12,
                }}
              />

              {/* Tags */}
              <div style={{ marginBottom: 12 }}>
                <Space size={8} wrap>
                  {(file.tags || []).map((tag: FileTag) => (
                    <Tag
                      key={tag.id}
                      closable={!readOnly}
                      closeIcon={
                        <CloseOutlined style={{ color: "#000000E0" }} />
                      }
                      onClose={(e) => {
                        e.preventDefault();
                        handleRemoveTag(file.uid, tag.id);
                      }}
                      style={{
                        backgroundColor: "#00000005",
                        border: `1px solid #00000026`,
                        cursor: readOnly ? "default" : "pointer",
                      }}
                    >
                      {tag.name}
                    </Tag>
                  ))}
                </Space>
              </div>

              {/* Action Buttons */}
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  // onClick={() => onDownload(file)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(file);
                  }}
                >
                  Download
                </Button>
                {!readOnly && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemove(file.uid)}
                  >
                    Delete
                  </Button>
                )}
              </Space>
            </div>
          </div>
          <Divider />
        </React.Fragment>
      ))}

      {/* Upload Button */}
      {!readOnly && (
        <div style={{ marginTop: 8 }}>
          <ImgCrop {...cropperParams}>
            <Upload
              accept={accept}
              multiple={false}
              showUploadList={false}
              beforeUpload={(file) => beforeUpload(file as RcFile)}
              fileList={[]}
            >
              <Button icon={<UploadOutlined />}>Upload photo</Button>
            </Upload>
          </ImgCrop>
        </div>
      )}

      {/* Hidden replace uploader */}
      {!readOnly && (
        <ImgCrop {...cropperParams}>
          <Upload
            className={styles.hiddenUpload}
            accept={accept}
            multiple={false}
            showUploadList={false}
            beforeUpload={(file) => {
              if (replacingIndex !== null) {
                beforeUpload(file as RcFile, replacingIndex);
                setReplacingIndex(null);
              }
              return false;
            }}
          >
            <Button ref={uploadRef} style={{ display: "none" }}>
              Replace
            </Button>
          </Upload>
        </ImgCrop>
      )}

      {/* Error Alert */}
      {uploader.error && (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          message={getMessageInError(uploader.error)}
          closable
          onClose={uploader.clearError}
        />
      )}

      {/* Preview Modal */}
      <Modal
        title={preview.title}
        footer={null}
        centered
        open={preview.visible}
        onCancel={handleCancelPreview}
        className="customModal"
      >
        <img alt="preview" style={{ width: "100%" }} src={preview.image} />
      </Modal>

      {/* Image Editor with Navigation */}
      <SimpleImageEditor
        open={editorOpen}
        image={editingFile?.preview || ""}
        fileId={editingFile?.uid}
        userId={user?.id}
        currentIndex={currentEditIndex + 1}
        totalCount={fileList.length}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveEdit}
        onPrev={handlePrevImage}
        onNext={handleNextImage}
      />
    </div>
  );
}

export default ImageUploadWithMetadata;
